<template>
    <div class="h-full flex flex-col space-y-4">
        <h1 class="shrink-0 text-2xl font-semibold text-gray-700">Customers</h1>

        <div class="flex shrink-0 items-center justify-between gap-2 border-b border-gray-200">
            <SavedViewTabs
                ref="viewTabs"
                page="customers"
                :current-filter="currentCustomerFilter"
                :can-manage="true"
                @apply="onApplyView"
            />
            <div class="flex items-center gap-2 pb-2">
                <div id="view-save-slot" class="flex items-center" />
                <OverlayFiltersFilterMenu :filters="filters" @update:filters="onFiltersChange" />
                <UInput
                    v-model="search"
                    type="text"
                    placeholder="Search..."
                    class="w-72"
                    icon="i-ph-magnifying-glass-duotone"
                />
            </div>
        </div>

        <div v-if="pinnedIds || filters.some(isFilterActive)" class="shrink-0 flex flex-wrap items-center gap-2">
            <div v-if="pinnedIds" class="flex items-center gap-1 rounded-full bg-primary-50 ring-1 ring-inset ring-primary-200 pl-2.5 pr-1 py-1 text-xs">
                <span class="font-medium text-primary-700">
                    {{ pinnedIds.length }} selected {{ pinnedIds.length === 1 ? 'customer' : 'customers' }}
                </span>
                <UButton
                    icon="i-ph-x-bold"
                    size="2xs"
                    color="primary"
                    variant="ghost"
                    :padded="false"
                    class="p-0.5"
                    @click="clearPinnedIds()"
                />
            </div>

            <FilterChips :filters="filters" @update:filters="onFiltersChange" />
        </div>

        <div class="flex-1 bg-white ring-1 ring-black/5 rounded-lg shadow-sm overflow-hidden">
            <UTable
                :rows="customers"
                :columns="columns"
                :loading="loading"
                :loading-state="{ icon: 'i-ph-circle-notch-bold', label: '' }"
                :empty-state="{ icon: 'i-ph-users-three-duotone', label: 'No customers.' }"
                :ui="{ thead: 'sticky top-0 bg-white z-10 shadow-sm', tbody: 'divide-y divide-gray-200', tr: { base: 'cursor-pointer hover:bg-gray-50' } }"
                class="h-full"
                @select="(row: Customer) => navigateTo(`/customers/${row.id}`)"
            >
                <template #id-data="{ row }">
                    <ULink :to="`/customers/${row.id}`" class="font-mono hover:underline">
                        {{ row.number.toString().padStart(8, '0') }}
                    </ULink>
                </template>
                <template #customer-data="{ row }">
                    <div class="leading-5">
                        <p>{{ [row.first_name, row.middle_name, row.last_name].filter(Boolean).join(' ') }}</p>
                        <span class="text-sm capitalize">{{ [row.gender, age(row.birth_date), row.blood_type].filter(Boolean).join(' · ') }}</span>
                    </div>
                </template>
                <template #contact-data="{ row }">
                    <div class="leading-5">
                        <PhoneLabel v-if="row.phone" :value="row.phone" />
                        <p v-if="row.email" :class="row.phone && 'text-sm'">{{ row.email }}</p>
                        <UBadge v-if="!row.email && !row.phone" color="orange" variant="subtle">
                            Unregistered
                        </UBadge>
                    </div>
                </template>
                <template #family-data="{ row }">
                    <div v-if="row.family_members" class="leading-5">
                        <p>{{ row.family_members.family?.name || '–' }}</p>
                        <FamilyMemberRoleBadge :role="row.family_members.role" size="xs" />
                    </div>
                    <span v-else class="text-gray-300">–</span>
                </template>
                <template #partner-data="{ row }">
                    <div v-if="currentPartner(row)" class="flex items-center space-x-2.5">
                        <Avatar bucket="media" folder="partners" size="sm" :placeholder="currentPartner(row)!.name" :path="currentPartner(row)!.avatar" />
                        <span>{{ currentPartner(row)!.name }}</span>
                    </div>
                    <span v-else class="text-gray-300">–</span>
                </template>
                <template #policy_holder-data="{ row }">
                    <div class="leading-5">
                        <p v-if="row?.policy_holder">{{ row.policy_holder }}</p>
                        <span v-else class="text-gray-300">–</span>
                    </div>
                </template>
                <template #subscription-data="{ row }">
                    <BadgeSubscriptionStatus with-expiry :subscription="row?.subscriptions?.find((s: Subscription) => s.id == row.subscription_id)" />
                </template>
                <template #switched_calls-header="{ column }">
                    <div class="flex items-center gap-1 whitespace-nowrap">
                        <span>{{ column.label }}</span>
                        <UTooltip :popper="{ placement: 'top' }" :ui="{ width: 'max-w-xs', base: 'h-auto whitespace-normal text-left normal-case font-normal tracking-normal p-2' }">
                            <UIcon name="i-ph-info-duotone" class="w-3.5 h-3.5 text-gray-400" />
                            <template #text>
                                Calls this member started that got switched to someone without a
                                membership. Stops counting once that person subscribes.
                            </template>
                        </UTooltip>
                    </div>
                </template>
                <template #switched_calls-data="{ row }">
                    <UBadge
                        v-if="switchedCallCounts[row.id]"
                        :label="String(switchedCallCounts[row.id])"
                        :color="switchedCallCounts[row.id] > 1 ? 'red' : 'orange'"
                        variant="subtle"
                    />
                    <span v-else class="text-gray-300">–</span>
                </template>
            </UTable>
        </div>

        <TablePagination :pagination="pagination" class="shrink-0" />
    </div>
</template>

<script setup lang="ts">
import { useStorage } from '@vueuse/core'
import { UserRole } from '~/types/user'
import { type Customer } from '~/types/customer'
import { watchDebounced } from '@vueuse/core'
import type { ListingStorage } from '~/types/common'
import type { Subscription } from '~/types/subscription'
import { FilterType, type Filter } from '~/types/filter'
import { chipsToCustomerFilter, customerFilterToChips } from '~/utils/customer-filter-chips'
import { buildMatchArgs } from '~/utils/customer-filter'
import { isFilterActive } from '~/utils/filter-chip-summary'

definePageMeta({ layout: 'default', middleware: 'auth', roles: [UserRole.admin, UserRole.medicalDirector, UserRole.doctor, UserRole.operator] })
useHead({ title: 'Customers' })

const client = useSupabaseClient()
const pagination = usePagination(50, { syncQuery: true })

const storage = useStorage('customers-list', {} as ListingStorage)

const { age } = useNumber()

const initialized = ref(false)
const loading = ref(false)
const search = ref('')
const customers = ref<Customer[]>([])
const suppressReset = ref(false)
const pinnedIds = ref<string[]>()
const switchedCallCounts = ref<Record<string, number>>({})

const defaultCustomerFilters = (): Filter[] => [
    { key: FilterType.subscriptionStatus, label: 'Subscription Status' },
    { key: FilterType.customerTag, label: 'Customer Tag' },
    { key: FilterType.partner, label: 'Partner' },
    { key: FilterType.policyHolder, label: 'Policy Holder' },
    { key: FilterType.age, label: 'Age' },
    { key: FilterType.gender, label: 'Gender' },
    { key: FilterType.country, label: 'Country' },
    { key: FilterType.switchedCalls, label: 'Non-member switches' },
]

const filters = ref<Filter[]>(defaultCustomerFilters())

const currentCustomerFilter = computed(() => chipsToCustomerFilter(filters.value))

function onApplyView(cf: any, initial = false) {
    if (initial) suppressReset.value = true
    filters.value = customerFilterToChips(cf ?? {}, defaultCustomerFilters())
}

function onFiltersChange(next: Filter[]) {
    filters.value = next
}

const columns = [
    { key: 'id', label: 'ID' },
    { key: 'customer', label: 'Customer' },
    { key: 'contact', label: 'Registered Contact' },
    { key: 'family', label: 'Family' },
    { key: 'partner', label: 'Partner' },
    { key: 'policy_holder', label: 'Policy Holder' },
    { key: 'subscription', label: 'Subscription' },
    { key: 'switched_calls', label: 'Non-member switches' },
]

onMounted(() => {
    if (storage.value.search) {
        search.value = storage.value.search
    }

    if (storage.value.ids) {
        pinnedIds.value = storage.value.ids
    }

    fetchData()

    nextTick(() => initialized.value = true)
})

watchDebounced(search, (val) => {
    if (initialized.value) {
        fetchData(true)
        storage.value.search = val
    }
}, { debounce: 600 })

watchDebounced(filters, () => {
    if (!initialized.value) return
    if (suppressReset.value) {
        suppressReset.value = false
        fetchData()
    } else {
        fetchData(true)
    }
}, { deep: true, debounce: 300 })

watch(() => pagination.page, () => {
    if (initialized.value) {
        fetchData()
    }
})

async function fetchData(resetPagination?: boolean) {
    if (resetPagination && pagination.reset()) {
        return
    }

    loading.value = true
    customers.value = []
    pagination.setTotal(undefined)
    switchedCallCounts.value = {}

    const cf = chipsToCustomerFilter(filters.value)
    const args = buildMatchArgs({ filter: cf, search: search.value })

    const richSelect = `id, number, user_id, subscription_id, first_name, middle_name, last_name, email, phone, avatar, birth_date, gender, blood_type, policy_holder, updated_at,
           family_members!family_members_customer_id_fkey (id, role, status, family_id, family:families (id, name)),
           subscriptions (id, source_id, source_type, is_active, expires_at, partner:partners (id, name, avatar))`

    let baseQuery = client
        .rpc('match_customers', args as never, { count: 'exact' }) as any

    if (pinnedIds.value) {
        baseQuery = baseQuery.in('id', pinnedIds.value)
    }

    const { count, data } = await baseQuery
        .select(richSelect)
        .order('number', { ascending: false })
        .range(pagination.from, pagination.to)

    if (count && data) {
        customers.value = data as unknown as Customer[]
        pagination.setTotal(count)
        await fetchSwitchedCallCounts((data as unknown as Customer[]).map(c => c.id))
    }

    loading.value = false
}

function currentPartner(customer: Customer) {
    return customer.subscriptions?.find(s => s.id === customer.subscription_id)?.partner
}

function clearPinnedIds() {
    pinnedIds.value = undefined
    storage.value.ids = undefined
    fetchData(true)
}

async function fetchSwitchedCallCounts(ids: string[]) {
    if (!ids.length) {
        return
    }

    const { data, error } = await client
        .from('membership_switch_counts')
        .select('customer_id, switched_call_count')
        .in('customer_id', ids)

    if (error) {
        console.error('Failed to load switched call counts', error)
        return
    }

    switchedCallCounts.value = Object.fromEntries(
        (data as unknown as { customer_id: string, switched_call_count: number }[])
            .map(row => [row.customer_id, row.switched_call_count]),
    )
}
</script>
